import { Head, router } from "@inertiajs/react"
import { Eye } from "lucide-react"
import { useState } from "react"

import { BuildingBeat } from "@/components/agents/setup/building-beat"
import { BookingStep } from "@/components/agents/setup/chatbot/booking-step"
import { CollectStep } from "@/components/agents/setup/chatbot/collect-step"
import type { CalendarEventType } from "@/components/agents/setup/chatbot/conversions-step"
import { InstallStep } from "@/components/agents/setup/chatbot/install-step"
import { PreviewPanel } from "@/components/agents/setup/chatbot/preview-sheet"
import { STEP_ORDER, stepFor } from "@/components/agents/setup/chatbot/steps"
import {
  type ChatbotSetupData,
  deriveBooking,
  deriveCollect,
} from "@/components/agents/setup/chatbot/types"
import { WizardActionContext } from "@/components/agents/setup/shell"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useIsMobile } from "@/hooks/use-mobile"
import AppLayout from "@/layouts/app-layout"
import { agentPath, setupAgentPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface SetupAgent {
  uid: string
  name: string
  setup_step: string | null
  setup_data: Record<string, unknown>
  archetype: { uid: string | null; slug: string | null; name: string | null }
}

interface RawEventType {
  uri?: string
  id?: string | number
  name?: string
  title?: string
  duration_minutes?: number | null
  length_in_minutes?: number | null
}

interface Props {
  agent: SetupAgent
  business_name: string | null
  // IMP-461/458: the scraped brand color seeds the widget's default look.
  brand_color: string | null
  connected_calendars: Record<string, boolean>
  calendly_event_types?: RawEventType[]
  cal_com_event_types?: RawEventType[]
}

// IMP-461: the assistant builds the chat bot from the business profile and
// brand; the beat shows once (setup untouched) and advances on its own.
const BUILDING_LINES = [
  "Analyzing your business…",
  "Matching your brand…",
  "Getting ready to greet your visitors…",
]

function defaultData(
  businessName: string | null,
  brandColor: string | null,
): ChatbotSetupData {
  return {
    bot_name: businessName ?? "",
    // IMP-461: appearance derives from the scraped brand (IMP-458); placement
    // defaults to bottom-right (editable post-launch from the drawer).
    primary_color: brandColor ?? "#2563EB",
    bubble_position: "bottom-right",
    welcome_message: "Hi there! How can I help you today?",
    popup_enabled: false,
    conversation_starters: [],
    response_style: "friendly",
    // IMP-463: the two independent capabilities (escalation is derived from
    // them server-side). Collect defaults on; booking is opt-in.
    collect_enabled: true,
    booking_enabled: false,
    appointment_calendar_kind: "",
    appointment_event_type_uri: "",
    embed_domain: "",
    custom_instructions: "",
  }
}

// Merge saved setup_data over the defaults so a returning user resumes their
// configuration; unknown/missing keys fall back to the default.
function initData(
  saved: Record<string, unknown>,
  businessName: string | null,
  brandColor: string | null,
): ChatbotSetupData {
  const base = defaultData(businessName, brandColor)
  const partial = saved as Partial<ChatbotSetupData>
  return {
    ...base,
    ...partial,
    // IMP-463: agents saved before the capability split carry only the legacy
    // goal/escalation keys — derive the two facts from them.
    collect_enabled: deriveCollect(partial),
    booking_enabled: deriveBooking(partial),
  }
}

function normalizeEventTypes(
  raw: RawEventType[] | undefined,
): CalendarEventType[] {
  return (raw ?? []).map((et) => {
    const value = String(et.uri ?? et.id ?? "")
    const minutes = et.duration_minutes ?? et.length_in_minutes
    const name = et.name ?? et.title ?? value
    return { value, label: minutes ? `${name} (${minutes} min)` : name }
  })
}

export default function WebsiteChatSetup({
  agent,
  business_name,
  brand_color,
  connected_calendars,
  calendly_event_types,
  cal_com_event_types,
}: Props) {
  const [stepIndex, setStepIndex] = useState(() =>
    STEP_ORDER.indexOf(stepFor(agent.setup_step)),
  )
  const [data, setData] = useState<ChatbotSetupData>(() =>
    initData(agent.setup_data ?? {}, business_name, brand_color),
  )
  const [processing, setProcessing] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const isMobile = useIsMobile()

  const isLast = stepIndex === STEP_ORDER.length - 1

  function handleUpdate(patch: Partial<ChatbotSetupData>) {
    setData((prev) => ({ ...prev, ...patch }))
  }

  // IMP-463: the wizard's two decisions ARE the stored capability toggles —
  // the edit drawer reads and writes the same two facts.
  function setCollectEnabled(on: boolean) {
    handleUpdate({ collect_enabled: on })
  }

  function setBookingEnabled(on: boolean) {
    handleUpdate({
      booking_enabled: on,
      ...(on
        ? {}
        : { appointment_calendar_kind: "", appointment_event_type_uri: "" }),
    })
  }

  // Persist the accumulated config to setup_data. On launch (complete) the
  // agent transitions draft -> operational server-side; otherwise we just
  // autosave so nothing is lost moving between steps or leaving the wizard.
  function persist(targetStep: string, complete = false) {
    setProcessing(true)
    router.patch(
      setupAgentPath({ id: agent.uid }),
      {
        setup_step: targetStep,
        complete,
        // ChatbotSetupData's values are all Inertia-serialisable scalars/arrays;
        // cast to satisfy the PATCH body's index-signature requirement.
        setup_data: data as unknown as Record<
          string,
          string | number | boolean | string[]
        >,
      },
      {
        preserveScroll: true,
        onFinish: () => setProcessing(false),
        onSuccess: () => {
          // On launch, land on the agent's own dashboard (IMP-338), not the
          // agents catalog.
          if (complete) router.visit(agentPath({ id: agent.uid }))
        },
      },
    )
  }

  function handleNext() {
    if (isLast) {
      persist(STEP_ORDER[stepIndex], true)
      return
    }
    const next = stepIndex + 1
    persist(STEP_ORDER[next])
    setStepIndex(next)
  }

  function handleBack() {
    if (stepIndex === 0) return
    const prev = stepIndex - 1
    persist(STEP_ORDER[prev])
    setStepIndex(prev)
  }

  const stepKey = STEP_ORDER[stepIndex]
  const common = {
    stepNumber: stepIndex + 1,
    totalSteps: STEP_ORDER.length,
    data,
    onUpdate: handleUpdate,
    onNext: handleNext,
    onBack: handleBack,
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { title: agent.name || "Website Chatbot", href: "#" },
  ]

  // A fresh agent (no step persisted yet) opens with the building beat, which
  // persists the first step when it finishes so a return visit skips it.
  if (!agent.setup_step) {
    return (
      <AppLayout breadcrumbs={breadcrumbs}>
        <Head title="Website Chatbot setup" />
        <BuildingBeat
          title="Building your website chat"
          lines={BUILDING_LINES}
          onDone={() => persist(STEP_ORDER[0])}
        />
      </AppLayout>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Website Chatbot setup" />

      <div className="h-app-viewport flex">
        <div className="min-w-0 flex-1">
          <WizardActionContext.Provider
            value={
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setPreviewOpen((v) => !v)}
              >
                <Eye className="size-3.5" />
                Preview
              </Button>
            }
          >
            {stepKey === "step-collect" && (
              <CollectStep
                stepNumber={common.stepNumber}
                totalSteps={common.totalSteps}
                value={data.collect_enabled}
                onChange={setCollectEnabled}
                onNext={handleNext}
                processing={processing}
              />
            )}
            {stepKey === "step-booking" && (
              <BookingStep
                {...common}
                processing={processing}
                value={data.booking_enabled}
                onChange={setBookingEnabled}
                connectedCalendars={connected_calendars}
                calendlyEventTypes={normalizeEventTypes(calendly_event_types)}
                calComEventTypes={normalizeEventTypes(cal_com_event_types)}
              />
            )}
            {stepKey === "step-install" && (
              <InstallStep
                {...common}
                processing={processing}
                agentUid={agent.uid}
                brandColor={brand_color}
              />
            )}
          </WizardActionContext.Provider>
        </div>

        {previewOpen && !isMobile && (
          <>
            <div className="shrink-0 border-l" />
            <div className="w-[440px] shrink-0">
              <PreviewPanel data={data} onClose={() => setPreviewOpen(false)} />
            </div>
          </>
        )}
      </div>

      {/* Mobile has no room for a second column — it would push the wizard off
          screen with no way back to it — so the preview opens over the step,
          dismissable by its own close button, the overlay, or Escape. */}
      {isMobile && (
        <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
          <SheetContent
            side="bottom"
            // PreviewPanel supplies its own titled header and close button.
            className="h-[85svh] gap-0 p-0 [&>button]:hidden"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Preview</SheetTitle>
            </SheetHeader>
            <PreviewPanel data={data} onClose={() => setPreviewOpen(false)} />
          </SheetContent>
        </Sheet>
      )}
    </AppLayout>
  )
}
